/**
 * Finds and returns the value associated with the first `true` boolean value in an array of tuples.
 * Each tuple contains a boolean condition and an associated value.
 *
 * @param array - An array of tuples where each tuple contains a boolean condition and a value
 * @returns The value from the first tuple where the boolean condition is true.
 *         If no true condition is found, returns undefined (for regular arrays)
 *         or the inferred type R (for arrays ending with [true, R])
 *
 * @example
 * ```typescript
 * const result = pickFirstTrueMatchingValue([
 *   [isOnline, "1"],
 *   [isHasIntroVideo, "2"],
 * ]);
 * // in that case result type is string | undefined;
 *
 * // Type safety: array must end with a true condition
 * // if you want the value not to be of type undefined
 * const result2 = pickFirstTrueMatchingValue([
 *   [isOnline, "1"],
 *   [isHasIntroVideo, "2"],
 *   [true, "3"] // Ensures a value will always be found
 * ]);
 * // in this case the type of the result2 is string
 * ```
 */
export const pickFirstTrueMatchingValue = <
  B extends boolean | undefined,
  V,
  Arr extends [...[B, V][], [true, V]] | [B, V][],
>(
  array: Arr,
): Arr extends [...[B, infer V][], [true, infer R]]
  ? R | V
  : Arr extends [B, infer Val][]
  ? Val | undefined
  : never => {
  const value = array.find(([bool]) => bool === true);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return value?.[1] as any;
};
